/**
 * useBudgetQueries — TanStack Query hooks for budget reference data and project schedules.
 *
 * Centralises master lists (departments, roles, equipment) and project schedule
 * reads used across BudgetSurv, BudgetReco, AssetCosts, ProjectSchedule, etc.
 */

import { useMemo } from 'react';
import { useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useQueryScope } from '../utils/queryScope';

const ROOT = 'budget';

const sharedQueryDefaults = {
  retry: 0,
};

function useScope() {
  return useQueryScope();
}

async function fetchBudgetMasterData(budgetContextArea) {
  const rolesPath = budgetContextArea
    ? `/personnel-roles?for_budget=1&context_area=${encodeURIComponent(budgetContextArea)}`
    : '/personnel-roles?for_budget=1';
  const [departmentsRes, rolesRes, assetsRes, categoriesRes] = await Promise.all([
    api.get('/personnel-departments'),
    api.get(rolesPath),
    api.get('/equipment-items'),
    api.get('/equipment-categories'),
  ]);
  return {
    departments: departmentsRes.data || [],
    roles: rolesRes.data || [],
    assetItems: assetsRes.data || [],
    categories: categoriesRes.data || [],
  };
}

// ============================================================================
// READ HOOKS
// ============================================================================

export function useBudgetMasterDataQuery(budgetContextArea = null, { enabled = true } = {}) {
  const scope = useScope();
  return useQuery({
    queryKey: [ROOT, scope, 'master-data', budgetContextArea],
    queryFn: () => fetchBudgetMasterData(budgetContextArea),
    enabled,
    staleTime: 5 * 60_000,
    ...sharedQueryDefaults,
  });
}

export function useEquipmentReference({ enabled = true } = {}) {
  const scope = useScope();
  return useQuery({
    queryKey: [ROOT, scope, 'equipment-reference'],
    queryFn: async () => {
      const [categoriesRes, itemsRes] = await Promise.all([
        api.get('/equipment-categories'),
        api.get('/equipment-items'),
      ]);
      return {
        categories: categoriesRes.data || [],
        items: itemsRes.data || [],
      };
    },
    enabled,
    staleTime: 5 * 60_000,
    ...sharedQueryDefaults,
  });
}

export function useProjectSchedule(projectId, { enabled = true } = {}) {
  const scope = useScope();
  const id = projectId != null ? parseInt(projectId, 10) : null;
  return useQuery({
    queryKey: [ROOT, scope, 'project-schedule', id],
    queryFn: async () => {
      const response = await api.get(`/projects/${id}/schedule`);
      return response.data;
    },
    enabled: enabled && !!id && !Number.isNaN(id),
    staleTime: 60_000,
    ...sharedQueryDefaults,
  });
}

export function useProjectBudgets(projectId, { enabled = true } = {}) {
  const scope = useScope();
  const id = projectId != null ? parseInt(projectId, 10) : null;
  return useQuery({
    queryKey: [ROOT, scope, 'project-budgets', id],
    queryFn: async () => {
      const { data } = await api.get(`/projects/${id}/budgets`);
      return Array.isArray(data) ? data : [];
    },
    enabled: enabled && !!id && !Number.isNaN(id),
    staleTime: 60_000,
    ...sharedQueryDefaults,
  });
}

export function useBudgetDetail(budgetId, { enabled = true } = {}) {
  const scope = useScope();
  const id = budgetId != null ? parseInt(budgetId, 10) : null;
  return useQuery({
    queryKey: [ROOT, scope, 'budget-detail', id],
    queryFn: async () => {
      const { data } = await api.get(`/budgets/${id}`);
      return data;
    },
    enabled: enabled && !!id && !Number.isNaN(id),
    staleTime: 60_000,
    ...sharedQueryDefaults,
  });
}

export function useDraftBudget(projectId, { enabled = true } = {}) {
  const scope = useScope();
  const id = projectId != null ? parseInt(projectId, 10) : null;
  return useQuery({
    queryKey: [ROOT, scope, 'draft-budget', id],
    // El error se propaga: el endpoint es `get_or_create` (`routers/projects.py:130`),
    // así que siempre hay borrador y un `null` sólo podía significar «falló».
    // Se pintaba como «este proyecto no tiene borrador», y con eso se empieza
    // uno nuevo encima del que sí existe (T4, M1).
    queryFn: async () => {
      const { data } = await api.get(`/projects/${id}/draft-budget`);
      return data;
    },
    enabled: enabled && !!id && !Number.isNaN(id),
    staleTime: 30_000,
    ...sharedQueryDefaults,
  });
}

async function fetchProjectBaseline(projectId, budgetType) {
  try {
    const response = await api.get(`/projects/${projectId}/budgets/baseline/${budgetType}`);
    return response?.data?.exists ? (response.data.baseline || null) : null;
  } catch (err) {
    if (err?.response?.status === 404) {
      return null;
    }
    throw err;
  }
}

export function useProjectBaseline(projectId, budgetType, { enabled = true } = {}) {
  const scope = useScope();
  const id = projectId != null ? parseInt(projectId, 10) : null;
  return useQuery({
    queryKey: [ROOT, scope, 'project-baseline', id, budgetType],
    queryFn: () => fetchProjectBaseline(id, budgetType),
    enabled: enabled && !!id && !Number.isNaN(id) && !!budgetType,
    staleTime: 60_000,
    ...sharedQueryDefaults,
  });
}

const APPROVED_BASELINE_TYPES = ['survey', 'drilling', 'recording'];

export function useApprovedBaselines(projectId, { enabled = true } = {}) {
  const scope = useScope();
  const id = projectId != null ? parseInt(projectId, 10) : null;
  const canFetch = enabled && !!id && !Number.isNaN(id);

  const results = useQueries({
    queries: APPROVED_BASELINE_TYPES.map((budgetType) => ({
      queryKey: [ROOT, scope, 'project-baseline', id, budgetType],
      queryFn: () => fetchProjectBaseline(id, budgetType),
      enabled: canFetch,
      staleTime: 60_000,
      ...sharedQueryDefaults,
    })),
  });

  return useMemo(() => {
    const byType = {};
    APPROVED_BASELINE_TYPES.forEach((type, index) => {
      byType[type] = results[index]?.data ?? null;
    });
    return {
      survey: byType.survey,
      drilling: byType.drilling,
      recording: byType.recording,
      isLoading: results.some((r) => r.isLoading),
      isFetching: results.some((r) => r.isFetching),
      error: results.find((r) => r.error)?.error ?? null,
      refetch: () => Promise.all(results.map((r) => r.refetch())),
    };
  }, [results]);
}

export function useApprovedHelicopterConfig(projectId, { enabled = true } = {}) {
  const scope = useScope();
  const id = projectId != null ? parseInt(projectId, 10) : null;
  return useQuery({
    queryKey: [ROOT, scope, 'approved-helicopter-config', id],
    queryFn: async () => {
      try {
        const { data } = await api.get(`/helicopter-config/project/${id}/approved`);
        return data || null;
      } catch (err) {
        if (err?.response?.status === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled: enabled && !!id && !Number.isNaN(id),
    staleTime: 60_000,
    ...sharedQueryDefaults,
  });
}

// ============================================================================
// INVALIDATION
// ============================================================================

export function useBudgetInvalidator() {
  const queryClient = useQueryClient();
  const scope = useScope();

  return useMemo(() => {
    const root = [ROOT, scope];
    const inv = (key) => queryClient.invalidateQueries({ queryKey: key });

    return {
      masterData: (budgetContextArea) =>
        budgetContextArea != null
          ? inv([...root, 'master-data', budgetContextArea])
          : inv([...root, 'master-data']),
      equipmentReference: () => inv([...root, 'equipment-reference']),
      projectSchedule: (projectId) =>
        projectId != null
          ? inv([...root, 'project-schedule', parseInt(projectId, 10)])
          : inv([...root, 'project-schedule']),
      projectBudgets: (projectId) =>
        projectId != null
          ? inv([...root, 'project-budgets', parseInt(projectId, 10)])
          : inv([...root, 'project-budgets']),
      budgetDetail: (budgetId) =>
        budgetId != null
          ? inv([...root, 'budget-detail', parseInt(budgetId, 10)])
          : inv([...root, 'budget-detail']),
      draftBudget: (projectId) =>
        projectId != null
          ? inv([...root, 'draft-budget', parseInt(projectId, 10)])
          : inv([...root, 'draft-budget']),
      projectBaseline: (projectId, budgetType) => {
        const id = projectId != null ? parseInt(projectId, 10) : null;
        if (id != null && budgetType) {
          return inv([...root, 'project-baseline', id, budgetType]);
        }
        return inv([...root, 'project-baseline']);
      },
      approvedHelicopterConfig: (projectId) =>
        projectId != null
          ? inv([...root, 'approved-helicopter-config', parseInt(projectId, 10)])
          : inv([...root, 'approved-helicopter-config']),
      all: () => inv(root),
    };
  }, [queryClient, scope]);
}
